import { routes } from "@/constants/url";
import type { User } from "@/hooks/auth/use-user";
import { supabase } from "@/lib/supabase";
import { TrackingEvent, trackEvent } from "@/lib/track/track-event";
import { SignInWithGoogleButton } from "@/routes/auth/components/sign-in-with-google-button";
import { PasswordSchema } from "@/utils/schema";
import { useToastMessage } from "@backpac/monica";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Typography,
} from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { PasswordInput } from "../components/password-input";
import { SeparatorWithText } from "../components/separator-with-text";

const EmailSignInSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  password: PasswordSchema,
});

type EmailSignInForm = z.infer<typeof EmailSignInSchema>;

function useRedirectUrl() {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect_url");

  return redirectUrl;
}

export function LoginPage() {
  const navigate = useNavigate();
  const redirectUrl = useRedirectUrl();

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3 w-full">
        <Typography asChild size="xl" font="unbounded">
          <h1>Sign in</h1>
        </Typography>
        <div className="flex items-center gap-1">
          <Typography asChild size="xs" weight={600}>
            <span>New to BackPac?</span>
          </Typography>
          <Typography asChild size="xs" weight={600}>
            <button type="button" className="underline" onClick={() => navigate(routes.AUTH.signup)}>
              Sign up here!
            </button>
          </Typography>
        </div>
      </div>

      <div className="w-full space-y-6">
        <SignInWithGoogleButton
          redirectTo={
            redirectUrl != null
              ? `${window.location.origin}${routes.AUTH.callback}?redirect_url=${redirectUrl}`
              : undefined
          }
        />
        <SeparatorWithText>OR</SeparatorWithText>
        <EmailSignInForm
          onSuccess={(user) => {
            trackEvent(TrackingEvent.SignIn, {
              name: user.user_metadata.name,
              email: user.email,
              method: "email",
            });
            if (redirectUrl != null) {
              window.location.href = redirectUrl;
            } else {
              navigate(routes.WORKSPACE_NAVIGATOR.root, { replace: true });
            }
          }}
        />
      </div>

      <Button
        variant="ghost"
        onClick={() => navigate(routes.AUTH.requestResetPassword)}
        className="underline mr-auto px-0"
      >
        Forgot your password?
      </Button>
    </div>
  );
}

function EmailSignInForm({ onSuccess }: { onSuccess?: (user: User) => void }) {
  const navigate = useNavigate();
  const { error } = useToastMessage();

  const form = useForm<EmailSignInForm>({
    resolver: zodResolver(EmailSignInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleEmailSignIn = async (values: EmailSignInForm) => {
    const response = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });

    if (response.error) {
      error(
        response.error.code === "invalid_credentials"
          ? "Password doesn't match the email. Please try again."
          : "Failed to sign in. Please try again.",
      );
    } else {
      onSuccess?.(response.data.user);
      navigate("/", { replace: true });
    }
  };

  return (
    <Form {...form}>
      <form
        id="email-sign-in-form"
        onSubmit={form.handleSubmit(handleEmailSignIn)}
        className="space-y-4"
        noValidate={true}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="Your email address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <Button
        form="email-sign-in-form"
        type="submit"
        size="lg"
        className="w-full"
        disabled={form.formState.isSubmitting}
      >
        Sign in
      </Button>
    </Form>
  );
}
